<script setup lang="ts">
import { Coupon } from '@/@types/cart'
import { Order, OrdersnapShot } from '@/@types/order'
import Badge from '@/components/ui/badge/Badge.vue'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { formatPrice } from '@/helpers/current-price'
import { formatDateTime } from '@/helpers/format-date'
import { cn } from '@/lib/utils'
import { Link, router, usePoll } from '@inertiajs/vue3'
import { Check } from 'lucide-vue-next'
import { ref } from 'vue'
import Resume from '../cart/payment-methods/Resume.vue'
import { PaymentHeader, PaymentStepper, PaymentTitle } from './payment-header'

interface OrderWithCoupon extends Order {
    coupon: Coupon
    order_snap_shot: OrdersnapShot
}

const { order } = defineProps<{ order: OrderWithCoupon }>()

const pixCode = ref<HTMLDivElement | null>(null)
const copied = ref(false)

async function handleCopy() {
    await navigator.clipboard.writeText(pixCode.value?.innerText ?? '')

    copied.value = true

    setTimeout(() => {
        copied.value = false
    }, 2000)
}

const { stop } = usePoll(15000, {
    only: ['order'],
    onFinish: () => {
        if (order.status !== 'pending') {
            stop()
            router.visit(`/orders/${order.id}`, {
                only: ['order'],
            })
        }
    },
})
</script>

<template>
    <main class="container" v-if="order.transaction_detail">
        <PaymentHeader :variant="order.status">
            <PaymentTitle :variant="order.status">
                {{ $t('Awaiting payment') }}
                <template #description>
                    <span
                        v-html="
                            $t(`Your order has been placed successfully. Order has been created and the Pix payment code has been generated.`, {
                                id: order.id,
                            })
                        "
                    ></span>
                </template>
            </PaymentTitle>
            <PaymentStepper
                :steps="[
                    {
                        name: $t('Order completed'),
                        state: 'completed',
                    },
                    {
                        name: $t('Make payment'),
                        state: 'active',
                    },
                    {
                        name: $t('Payment received'),
                        state: 'inactive',
                    },
                ]"
            />
        </PaymentHeader>

        <section class="mt-6 flex flex-wrap gap-8">
            <div class="flex flex-1 flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-8">
                <div class="mx-auto flex max-w-xl flex-col items-center justify-center">
                    <Badge
                        :variant="order.status"
                        class="rounded-sm border-0 before:size-1.5 before:rounded-full before:bg-yellow-400 before:content-['']"
                    >
                        {{ $t(order.status) }}
                    </Badge>

                    <h2 class="mt-4 mb-2.5 text-center font-sans text-2xl font-semibold">
                        {{ $t('Amount to pay:', { amount: formatPrice(order.transaction_detail.amount / 100) }) }}
                    </h2>

                    <p class="text-center text-[#444]">
                        {{ $t('Expires in') }}: {{ formatDateTime(order.transaction_detail.expiration_date, $i18n.locale) }}
                    </p>
                </div>

                <div class="mt-8 flex w-full flex-col items-center gap-6 text-sm md:flex-row">
                    <div class="size-[270px]">
                        <img :src="order.transaction_detail.qr_image_url" alt="QR code for Pix payment" />
                    </div>
                    <div class="flex-1">
                        <ul class="space-y-4">
                            <li class="flex items-center gap-4">
                                <div
                                    class="flex size-10 min-w-10 items-center justify-center rounded-full border border-slate-200 font-sans text-xl font-semibold"
                                >
                                    1
                                </div>

                                {{ $t('Pix instructions.1') }}
                            </li>

                            <li class="flex items-center gap-4">
                                <div
                                    class="flex size-10 min-w-10 items-center justify-center rounded-full border border-slate-200 font-sans text-xl font-semibold"
                                >
                                    2
                                </div>

                                {{ $t('Pix instructions.2') }}
                            </li>

                            <li class="flex items-center gap-4">
                                <div
                                    class="flex size-10 min-w-10 items-center justify-center rounded-full border border-slate-200 font-sans text-xl font-semibold"
                                >
                                    3
                                </div>

                                {{ $t('Pix instructions.3') }}
                            </li>
                        </ul>
                    </div>
                </div>

                <Separator class="mt-8 mb-4 lg:mb-12" />

                <div class="flex flex-col items-center justify-between gap-6 bg-gray-50 p-4 md:flex-row">
                    <div ref="pixCode" class="text-sm break-all">
                        {{ order.transaction_detail.qr_text }}
                    </div>

                    <Button @click="handleCopy" :class="cn('relative w-full max-w-36', copied ? 'bg-emerald-500 hover:bg-emerald-500' : '')">
                        <div :class="cn('transition-all', copied ? 'scale-100 opacity-100' : 'scale-0 opacity-0')" aria-label="copied">
                            <Check :size="24" :stroke-width="2" aria-hidden="true" />
                        </div>
                        <div :class="cn('absolute transition-all', copied ? 'scale-0 opacity-0' : 'scale-100 opacity-100')">
                            {{ $t('Copy code') }}
                        </div>
                    </Button>
                </div>

                <Button :as="Link" href="?show_order_details=true" class="mt-6">
                    {{ $t('View order details') }}
                </Button>
            </div>
            <Resume :order="order" />
        </section>
    </main>
</template>
